/**
 * DOMOptimizer.ts - DOM update optimization for Stylr
 * 
 * CHANGE HISTORY:
 * - 251102_1646 danielbeer: Created for Phase 3.4 performance optimization
 * 
 * Provides:
 * - Batched DOM updates
 * - Debounced style application
 * - RequestAnimationFrame scheduling
 * - Minimal reflow/repaint
 */

/**
 * Batch DOM updates using requestAnimationFrame
 */
export class DOMBatcher {
  private pending: Map<string, () => void>;
  private rafId: number | null = null;

  constructor() {
    this.pending = new Map();
  }

  /**
   * Schedule a DOM update
   */
  schedule(key: string, update: () => void): void {
    this.pending.set(key, update);
    
    if (this.rafId === null) {
      this.rafId = requestAnimationFrame(() => this.flush());
    }
  }

  /**
   * Execute all pending updates
   */
  private flush(): void {
    const updates = Array.from(this.pending.values());
    this.pending.clear();
    this.rafId = null;
    
    // Execute all updates in one frame
    updates.forEach(update => update());
  }

  /**
   * Cancel all pending updates
   */
  cancel(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.pending.clear();
  }

  /**
   * Get pending update count
   */
  getPendingCount(): number {
    return this.pending.size;
  }
}

/**
 * Debounce function calls
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      fn(...args);
      timeoutId = null;
    }, delay);
  };
}

/**
 * Throttle function calls
 */
export function throttle<T extends (...args: any[]) => any>(
  fn: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

/**
 * Optimized CSS variable setter
 * Batches multiple setProperty calls
 */
export class CSSVariableSetter {
  private batcher: DOMBatcher;
  private pendingVars: Map<string, string>;

  constructor() {
    this.batcher = new DOMBatcher();
    this.pendingVars = new Map();
  }

  /**
   * Set a CSS variable (batched)
   */
  set(name: string, value: string): void {
    this.pendingVars.set(name, value);
    
    this.batcher.schedule('css-vars', () => {
      this.applyPendingVars();
    });
  }

  /**
   * Set multiple CSS variables at once
   */
  setMultiple(vars: Record<string, string>): void {
    Object.entries(vars).forEach(([name, value]) => {
      this.pendingVars.set(name, value);
    });
    
    this.batcher.schedule('css-vars', () => {
      this.applyPendingVars();
    });
  }

  /**
   * Apply all pending variables in one DOM operation
   */
  private applyPendingVars(): void {
    if (this.pendingVars.size === 0) return;
    
    const root = document.documentElement;
    const style = root.style;
    
    // Batch all setProperty calls
    this.pendingVars.forEach((value, name) => {
      style.setProperty(name, value);
    });
    
    this.pendingVars.clear();
  }

  /**
   * Force immediate application
   */
  flush(): void {
    this.batcher.cancel();
    this.applyPendingVars();
  }
}

/**
 * Performance monitor for DOM operations
 */
export class DOMPerformanceMonitor {
  private measurements: Map<string, number[]>;

  constructor() {
    this.measurements = new Map();
  }

  /**
   * Measure a DOM operation
   */
  measure<T>(name: string, fn: () => T): T {
    const start = performance.now();
    const result = fn();
    const duration = performance.now() - start;
    
    if (!this.measurements.has(name)) {
      this.measurements.set(name, []);
    }
    this.measurements.get(name)!.push(duration);
    
    return result;
  }

  /**
   * Measure an async DOM operation
   */
  async measureAsync<T>(name: string, fn: () => Promise<T>): Promise<T> {
    const start = performance.now();
    const result = await fn();
    const duration = performance.now() - start;
    
    if (!this.measurements.has(name)) {
      this.measurements.set(name, []);
    }
    this.measurements.get(name)!.push(duration);
    
    return result;
  }

  /**
   * Get statistics for an operation
   */
  getStats(name: string) {
    const measurements = this.measurements.get(name);
    if (!measurements || measurements.length === 0) {
      return null;
    }
    
    const sorted = [...measurements].sort((a, b) => a - b);
    const sum = measurements.reduce((a, b) => a + b, 0);
    
    return {
      count: measurements.length,
      avg: sum / measurements.length,
      min: sorted[0],
      max: sorted[sorted.length - 1],
      median: sorted[Math.floor(sorted.length / 2)],
      p95: sorted[Math.floor(sorted.length * 0.95)]
    };
  }

  /**
   * Get all statistics
   */
  getAllStats() {
    const stats: Record<string, any> = {};
    this.measurements.forEach((_, name) => {
      stats[name] = this.getStats(name);
    });
    return stats;
  }

  /**
   * Reset measurements
   */
  reset(): void {
    this.measurements.clear();
  }
}

/**
 * Singleton instances
 */
let batcherInstance: DOMBatcher | null = null;
let cssSetterInstance: CSSVariableSetter | null = null;
let monitorInstance: DOMPerformanceMonitor | null = null;

export function getDOMBatcher(): DOMBatcher {
  if (!batcherInstance) {
    batcherInstance = new DOMBatcher();
  }
  return batcherInstance;
}

export function getCSSVariableSetter(): CSSVariableSetter {
  if (!cssSetterInstance) {
    cssSetterInstance = new CSSVariableSetter();
  }
  return cssSetterInstance;
}

export function getDOMPerformanceMonitor(): DOMPerformanceMonitor {
  if (!monitorInstance) {
    monitorInstance = new DOMPerformanceMonitor();
  }
  return monitorInstance;
}

export function resetDOMOptimizers(): void {
  batcherInstance = null;
  cssSetterInstance = null;
  monitorInstance = null;
}
